// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { EditableProfile, ProfileRole } from '@/types/profileTypes';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerClose,
  DrawerFooter,
} from '@/components/ui/drawer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { X, Loader2, Save, Crown, AlertCircle, Key, UserX, UserPlus } from 'lucide-react';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import AdminPasswordReset from '@/components/admin/users/AdminPasswordReset';
import { useRolesAndMemberships } from '@/hooks/useRolesAndMemberships';
import { useRoleAssignments } from '@/hooks/useRoleAssignments';
import { MultiMembershipSelector } from '@/components/admin/MultiMembershipSelector';
import { Database } from '@/integrations/supabase/types';
import { invokeEdgeFunction } from "@/lib/invokeEdgeFunction";
import { getSessionUser } from '@/lib/authSession';

type SupabaseMembership = Database['public']['Tables']['memberships']['Row'];

interface EditableUserDetailDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  user: EditableProfile | null;
  onUserUpdate: (updatedUser: EditableProfile) => void;
  onDataRefresh?: () => void;
  inline?: boolean;
}

const EditableUserDetailDrawer: React.FC<EditableUserDetailDrawerProps> = ({
  isOpen,
  onClose,
  user,
  onUserUpdate,
  onDataRefresh,
  inline = false,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [editableUser, setEditableUser] = useState<EditableProfile | null>(null);
  const [adminNotes, setAdminNotes] = useState<string>('');
  const [selectedMemberships, setSelectedMemberships] = useState<string[]>([]);
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [primaryRole, setPrimaryRole] = useState<string | null>(null);
  const [primaryMembership, setPrimaryMembership] = useState<string | null>(null);
  const [passwordResetOpen, setPasswordResetOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { roles, memberships, isLoading: rolesLoading, error: rolesError } = useRolesAndMemberships();
  const {
    roleAssignments,
    profileMemberships,
    isLoading: assignmentsLoading,
    assignMembership,
    removeMembership,
    refreshAssignments
  } = useRoleAssignments(user?.id || null);

  // Debug logging
  useEffect(() => {
    console.log('🔍 EditableUserDetailDrawer Debug Info:');
    console.log('- User:', user);
    console.log('- Roles loading:', rolesLoading);
    console.log('- Roles data:', roles);
    console.log('- Memberships data:', memberships);
    console.log('- Assignments loading:', assignmentsLoading);
    console.log('- Profile memberships:', profileMemberships);
    console.log('- Roles error:', rolesError);
  }, [user, rolesLoading, roles, memberships, assignmentsLoading, profileMemberships, rolesError]);

  useEffect(() => {
    if (user) {
      console.log('🔍 EditableUserDetailDrawer: User ID for assignments:', user.id);
      setEditableUser(user);
      setLocalError(null);
      loadAdminNotes(user.user_id || user.id);
    }
  }, [user]);

  useEffect(() => {
    if (profileMemberships) {
      const currentMembershipSlugs = profileMemberships.map(m => m.membership_slug);
      setSelectedMemberships(currentMembershipSlugs);
      console.log('🔄 Initialized selected memberships:', currentMembershipSlugs);
    }
  }, [profileMemberships]);

  useEffect(() => {
    if (editableUser?.active_membership_slug) {
      setPrimaryMembership(editableUser.active_membership_slug);
      console.log('🔄 Initialized primary membership:', editableUser.active_membership_slug);
    }
  }, [editableUser?.active_membership_slug]);

  useEffect(() => {
    if (roleAssignments && roleAssignments.length > 0) {
      const currentRoleSlugs = roleAssignments.map(r => r.role_slug);
      setSelectedRoles(currentRoleSlugs);
      console.log('🔄 Initialized selected roles from assignments:', currentRoleSlugs);
    } else if (editableUser?.role) {
      setSelectedRoles([editableUser.role]);
      console.log('🔄 Initialized selected roles from profile role:', editableUser.role);
    }
  }, [roleAssignments, editableUser?.role]);

  useEffect(() => {
    if (editableUser?.role) {
      setPrimaryRole(editableUser.role);
      console.log('🔄 Initialized primary role:', editableUser.role);
    }
  }, [editableUser?.role]);

  useEffect(() => {
    if (rolesError) {
      console.error('❌ EditableUserDetailDrawer: Roles error:', rolesError);
      setLocalError(`Error fetching roles: ${rolesError}`);
      toast.error(`Error fetching roles: ${rolesError}`);
    }
  }, [rolesError]);

  const loadAdminNotes = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('admin_user_notes')
        .select('note')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (data && !error) {
        setAdminNotes(data.note || '');
      }
    } catch (err) {
      console.error('Error loading admin notes:', err);
    }
  };

  const saveAdminNotes = async () => {
    if (!user?.user_id) return; // Use user_id instead of profile id

    try {
      const { data: { user: currentUser } } = await getSessionUser();
      
      const { error } = await supabase
        .from('admin_user_notes')
        .upsert({
          user_id: user.user_id, // This should be the auth.users.id
          admin_id: currentUser?.id,
          note: adminNotes
        });

      if (error) {
        console.error('Error saving admin notes:', error);
        toast.error('Failed to save admin notes');
      } else {
        toast.success('Admin notes saved');
      }
    } catch (err) {
      console.error('Error saving admin notes:', err);
      toast.error('Failed to save admin notes');
    }
  };

  const createAuthUser = async () => {
    if (!editableUser) return;
    
    try {
      setIsSubmitting(true);
      const { data, error } = await invokeEdgeFunction('admin-create-auth-user', {
        body: { profile_id: editableUser.id }
      });

      if (error) throw error;

      toast.success('Auth user created successfully');
      setEditableUser({ ...editableUser, user_id: data.user_id });
      if (onDataRefresh) onDataRefresh();
    } catch (error: any) {
      console.error('Error creating auth user:', error);
      toast.error(error.message || 'Failed to create auth user');
    } finally {
      setIsSubmitting(false);
    }
  };

  const suspendUser = async () => {
    if (!editableUser) return;

    try {
      const { error } = await supabase
        .from('profiles')
        .update({ status: 'banned' })
        .eq('id', editableUser.id);

      if (error) {
        console.error('Error suspending user:', error);
        toast.error('Failed to suspend user');
      } else {
        setEditableUser(prev => prev ? { ...prev, status: 'banned' } : null);
        toast.success('User suspended successfully');
      }
    } catch (err) {
      console.error('Error suspending user:', err);
      toast.error('Failed to suspend user');
    }
  };

  // Show error state if there's a critical error
  if (!user && isOpen) {
    return (
      <Drawer open={isOpen} onOpenChange={onClose}>
        <DrawerContent>
          <DrawerHeader className="flex justify-between items-center">
            <DrawerTitle>Error Loading User</DrawerTitle>
            <DrawerClose asChild>
              <Button variant="ghost" size="sm">
                <X className="h-4 w-4" />
              </Button>
            </DrawerClose>
          </DrawerHeader>
          <div className="p-4">
            <div className="flex items-center gap-2 text-red-600">
              <AlertCircle className="h-5 w-5" />
              <span>Failed to load user data. Please try again.</span>
            </div>
          </div>
          <DrawerFooter>
            <Button onClick={onClose}>Close</Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    );
  }

  const handleInputChange = (field: keyof EditableProfile, value: string | number | null) => {
    setEditableUser(prev => prev ? { ...prev, [field]: value } : null);
  };

  const handleRoleChange = (newRole: string) => {
    const roleValue = newRole as ProfileRole;
    setEditableUser(prev => prev ? { 
      ...prev, 
      role: roleValue,
      primary_role_slug: roleValue 
    } : null);
  };

  const handleSave = async () => {
    if (!editableUser) {
      console.error('❌ No editable user data to save');
      return;
    }

    setIsUpdating(true);
    setLocalError(null);

    try {
      console.log('💾 Saving user data:', editableUser);
      
      // Create clean update data with proper null handling for dates and non-existent fields
      const updateData: Record<string, any> = {};
      
      // Basic string fields
      if (editableUser.name !== undefined) {
        updateData.name = editableUser.name?.trim() || null;
      }
      if (editableUser.username !== undefined) {
        updateData.username = editableUser.username?.trim() || null;
      }
      if (editableUser.email !== undefined) {
        updateData.email = editableUser.email?.trim() || null;
      }
      if (editableUser.phone !== undefined) {
        updateData.phone = editableUser.phone?.trim() || null;
      }
      if (editableUser.city !== undefined) {
        updateData.city = editableUser.city?.trim() || null;
      }
      if (editableUser.state !== undefined) {
        updateData.state = editableUser.state?.trim() || null;
      }
      if (editableUser.role !== undefined) {
        updateData.role = editableUser.role;
      }
      if (editableUser.status !== undefined) {
        updateData.status = editableUser.status?.trim() || null;
      }
      if (editableUser.personal_statement !== undefined) {
        updateData.personal_statement = editableUser.personal_statement?.trim() || null;
      }
      if (editableUser.education_level !== undefined) {
        updateData.education_level = editableUser.education_level?.trim() || null;
      }
      
      // Date fields - convert empty strings to null
      if (editableUser.birthdate !== undefined) {
        updateData.birthdate = editableUser.birthdate?.trim() || null;
      }
      
      // Numeric fields
      if (editableUser.grad_year !== undefined) {
        updateData.grad_year = editableUser.grad_year || null;
      }
      if (editableUser.height_inches !== undefined) {
        updateData.height_inches = editableUser.height_inches || null;
      }
      if (editableUser.weight_lbs !== undefined) {
        updateData.weight_lbs = editableUser.weight_lbs || null;
      }

      console.log('🧹 Clean update data:', updateData);
      
      // If email is being updated, we need to update it in auth.users as well
      if (updateData.email && editableUser.email !== user?.email) {
        console.log('📧 Email changed, updating auth.users table...');
        
        const { data: authUpdateResult, error: authUpdateError } = await invokeEdgeFunction('admin-auth-management', {
          body: {
            action: 'update_email',
            userId: editableUser.user_id,
            email: updateData.email
          }
        });

        if (authUpdateError) {
          console.error('❌ Error updating auth email:', authUpdateError);
          toast.error(`Failed to update authentication email: ${authUpdateError.message}`);
          return;
        }

        if (authUpdateResult?.error) {
          console.error('❌ Error updating auth email:', authUpdateResult.error);
          toast.error(`Failed to update authentication email: ${authUpdateResult.error}`);
          return;
        }

        console.log('✅ Auth email update result:', authUpdateResult);
      }
      
      const { data: updateResult, error } = await supabase
        .from('profiles')
        .update(updateData)
        .eq('id', editableUser.id)
        .select();

      if (error) {
        console.error('❌ Error updating user:', error);
        console.error('❌ Update data:', updateData);
        console.error('❌ User ID:', editableUser.id);
        setLocalError(`Failed to update user: ${error.message}`);
        toast.error(`Failed to update user: ${error.message}`);
        return;
      }
      
      console.log('✅ Profile update result:', updateResult);

      // Save admin notes
      await saveAdminNotes();

      // After the successful profile update
      console.log('📋 Saving membership changes...');
      console.log('Current memberships:', profileMemberships?.map(m => m.membership_slug) || []);
      console.log('Selected memberships:', selectedMemberships);

      // Find which memberships to add/remove
      const currentMembershipSlugs = profileMemberships?.map(m => m.membership_slug) || [];
      const membershipsToAdd = selectedMemberships.filter(m => !currentMembershipSlugs.includes(m));
      const membershipsToRemove = currentMembershipSlugs.filter(m => !selectedMemberships.includes(m));

      console.log('Memberships to add:', membershipsToAdd);
      console.log('Memberships to remove:', membershipsToRemove);

      // Add new memberships (check for existing inactive records first)
      for (const membershipSlug of membershipsToAdd) {
        console.log('➕ Adding membership:', membershipSlug);
        
        // Check if an inactive record already exists
        const { data: existingRecord } = await supabase
          .from('profile_memberships')
          .select('id')
          .eq('profile_id', editableUser.id)
          .eq('membership_slug', membershipSlug)
          .maybeSingle();
        
        let error;
        if (existingRecord) {
          // Update existing record - reactivate it
          const result = await supabase
            .from('profile_memberships')
            .update({
              is_active: true,
              expires_at: null,
              started_at: new Date().toISOString(),
              assigned_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            })
            .eq('id', existingRecord.id);
          error = result.error;
        } else {
          // Insert new record
          const result = await supabase
            .from('profile_memberships')
            .insert({
              profile_id: editableUser.id,
              membership_slug: membershipSlug,
              is_active: true
            });
          error = result.error;
        }
          
        if (error) {
          console.error('Failed to add membership:', error);
          toast.error(`Failed to add membership ${membershipSlug}: ${error.message}`);
        } else {
          console.log('✅ Successfully added membership:', membershipSlug);
        }
      }

      // Remove unchecked memberships
      for (const membershipSlug of membershipsToRemove) {
        console.log('➖ Removing membership:', membershipSlug);
        const { error } = await supabase
          .from('profile_memberships')
          .update({ is_active: false })
          .match({
            profile_id: editableUser.id,
            membership_slug: membershipSlug
          });
          
        if (error) {
          console.error('Failed to remove membership:', error);
          toast.error(`Failed to remove membership ${membershipSlug}: ${error.message}`);
        } else {
          console.log('✅ Successfully removed membership:', membershipSlug);
        }
      }

      // RLevents migration: role_assignments table dropped — role mutation is now no-op.
      // Roles are managed directly via user_roles in Supabase.
      console.log('📋 Role changes are no-ops in RLevents (role_assignments table removed).');

      // Save primary role to profiles table
      if (primaryRole && selectedRoles.includes(primaryRole)) {
        console.log('⭐ Setting primary role:', primaryRole);
        const { error } = await supabase
          .from('profiles')
          .update({ role: primaryRole })
          .eq('id', editableUser.id);
          
        if (error) {
          console.error('Failed to set primary role:', error);
          toast.error(`Failed to set primary role: ${error.message}`);
        } else {
          console.log('✅ Successfully set primary role:', primaryRole);
        }
      }

      // Save primary membership to profiles table
      if (primaryMembership && selectedMemberships.includes(primaryMembership)) {
        console.log('⭐ Setting primary membership:', primaryMembership);
        console.log('⭐ selectedMemberships includes primary?', selectedMemberships.includes(primaryMembership));
        console.log('⭐ Profile ID:', editableUser.id);
        console.log('⭐ User ID:', editableUser.user_id);
        
        // First check what's currently in the database
        const { data: currentData, error: fetchError } = await supabase
          .from('profiles')
          .select('id, active_membership_slug, user_id')
          .eq('id', editableUser.id)
          .single();
        
        console.log('🔍 Current database data before update:', currentData);
        
        // Also update membership_level for display consistency
        const { error } = await supabase
          .from('profiles')
          .update({ 
            active_membership_slug: primaryMembership,
            membership_level: primaryMembership 
          })
          .eq('id', editableUser.id);
          
        if (error) {
          console.error('❌ Failed to set primary membership:', error);
          toast.error(`Failed to set primary membership: ${error.message}`);
        } else {
          console.log('✅ Successfully set primary membership:', primaryMembership);
          
          // Verify the update worked by fetching the data again
          const { data: verifyData, error: verifyError } = await supabase
            .from('profiles')
            .select('id, active_membership_slug, membership_level')
            .eq('id', editableUser.id)
            .single();
          
          console.log('🔍 Database data after update:', verifyData);
          
          if (verifyError) {
            console.error('❌ Failed to verify update:', verifyError);
          }
        }
      } else {
        console.log('🔍 Primary membership not saved because:');
        console.log('- primaryMembership:', primaryMembership);
        console.log('- selectedMemberships:', selectedMemberships);
        console.log('- primaryMembership in selected?', selectedMemberships.includes(primaryMembership || ''));
      }

      console.log('✅ User updated successfully');
      
      // Force data refresh first, then close
      if (onDataRefresh) {
        await onDataRefresh();
      }
      
      // Update the user state with the saved changes
      onUserUpdate({ ...editableUser });
      
      toast.success('User updated successfully');
      onClose();
    } catch (err) {
      console.error('❌ Error saving user:', err);
      setLocalError('An unexpected error occurred');
      toast.error('An unexpected error occurred');
    } finally {
      setIsUpdating(false);
    }
  };

  const getRoleName = (slug: string) => {
    const role = roles.find(r => r.slug === slug);
    return role ? role.name : slug || 'Unknown';
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'admin': return 'bg-red-100 text-red-800';
      case 'coach': return 'bg-blue-100 text-blue-800';
      case 'parent': return 'bg-green-100 text-green-800';
      case 'vendor': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const handleAssignMembership = async (membershipSlug: string) => {
    console.log('📝 Local membership assignment:', membershipSlug);
    setSelectedMemberships(prev => {
      const updated = [...prev, membershipSlug];
      console.log('Updated selected memberships:', updated);
      return updated;
    });
    return true;
  };

  const handleRemoveMembership = async (membershipSlug: string) => {
    console.log('📝 Local membership removal:', membershipSlug);
    setSelectedMemberships(prev => {
      const updated = prev.filter(m => m !== membershipSlug);
      console.log('Updated selected memberships:', updated);
      return updated;
    });
    // If removing the primary membership, clear it
    if (primaryMembership === membershipSlug) {
      setPrimaryMembership(null);
    }
    return true;
  };

  const handleSetPrimary = (membershipSlug: string) => {
    console.log('⭐ handleSetPrimary called with:', membershipSlug);
    console.log('⭐ Current primaryMembership state:', primaryMembership);
    console.log('⭐ Current selectedMemberships:', selectedMemberships);
    setPrimaryMembership(membershipSlug);
    console.log('⭐ Setting primary membership to:', membershipSlug);
    // Ensure this membership is also selected
    if (!selectedMemberships.includes(membershipSlug)) {
      setSelectedMemberships(prev => [...prev, membershipSlug]);
      console.log('⭐ Added membership to selected list:', membershipSlug);
    }
  };

  const handleRoleToggle = (roleSlug: string, checked: boolean) => {
    if (checked) {
      setSelectedRoles(prev => [...prev, roleSlug]);
    } else {
      setSelectedRoles(prev => prev.filter(r => r !== roleSlug));
      // If removing the primary role, clear it
      if (primaryRole === roleSlug) {
        setPrimaryRole(null);
      }
    }
  };

  const handleSetPrimaryRole = (roleSlug: string) => {
    console.log('⭐ Setting primary role:', roleSlug);
    setPrimaryRole(roleSlug);
    // Ensure this role is also selected
    if (!selectedRoles.includes(roleSlug)) {
      setSelectedRoles(prev => [...prev, roleSlug]);
    }
  };

  if (!editableUser) return null;

  console.log('🎨 UserProfileModal: Rendering with data:', {
    editableUser,
    profileMemberships,
    memberships,
    assignmentsLoading
  });

  const content = (
    <div className="space-y-6">
      {editableUser ? (
        <>
          {/* Basic Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium">Basic Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="name">Full Name</Label>
                <Input
                  id="name"
                  value={editableUser.name || ''}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="username">Username</Label>
                <Input
                  id="username"
                  value={editableUser.username || ''}
                  onChange={(e) => handleInputChange('username', e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={editableUser.email || ''}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  value={editableUser.phone || ''}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="city">City</Label>
                <Input
                  id="city"
                  value={editableUser.city || ''}
                  onChange={(e) => handleInputChange('city', e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="state">State</Label>
                <Input
                  id="state"
                  value={editableUser.state || ''}
                  onChange={(e) => handleInputChange('state', e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="grad_year">Graduation Year</Label>
                <Input
                  id="grad_year"
                  type="number"
                  value={editableUser.grad_year || ''}
                  onChange={(e) => handleInputChange('grad_year', e.target.value ? parseInt(e.target.value) : null)}
                />
              </div>
              <div>
                <Label htmlFor="status">Status</Label>
                <Select
                  value={editableUser.status || 'active'}
                  onValueChange={(value) => handleInputChange('status', value)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="banned">Banned</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Primary Role Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium">Primary Role</h3>
            <div>
              <Label htmlFor="role">Primary Role</Label>
              <Select
                value={editableUser?.role || 'athlete'}
                onValueChange={handleRoleChange}
                disabled={rolesLoading}
              >
                <SelectTrigger>
                  <SelectValue>
                    <Badge className={getRoleBadgeColor(editableUser?.role || 'athlete')}>
                      {getRoleName(editableUser?.role || 'athlete')}
                    </Badge>
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {roles.map(role => (
                    <SelectItem key={role.slug} value={role.slug}>
                      {role.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Admin Actions */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium">Admin Actions</h3>
            <div className="flex gap-2 flex-wrap">
              {!editableUser.user_id && (
                <Button
                  variant="default"
                  size="sm"
                  onClick={createAuthUser}
                  className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700"
                >
                  <Key className="h-4 w-4" />
                  Create Auth User
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPasswordResetOpen(true)}
                className="flex items-center gap-2"
                disabled={!editableUser.email}
              >
                <Key className="h-4 w-4" />
                Reset Password
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={suspendUser}
                className="flex items-center gap-2 text-red-600 hover:text-red-700"
                disabled={editableUser.status === 'banned'}
              >
                <UserX className="h-4 w-4" />
                {editableUser.status === 'banned' ? 'Already Suspended' : 'Suspend User'}
              </Button>
            </div>
          </div>

          {/* Admin Notes */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium">Admin Notes</h3>
            <div>
              <Label htmlFor="adminNotes">Internal Notes</Label>
              <Textarea
                id="adminNotes"
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                placeholder="Add internal admin notes here..."
                rows={3}
              />
              <p className="text-xs text-muted-foreground mt-1">
                These notes are only visible to administrators.
              </p>
            </div>
          </div>

          {/* Membership Management Section */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Crown className="h-5 w-5 text-yellow-500" />
              <h3 className="text-lg font-medium">Membership Management</h3>
            </div>
            <div className="p-4 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-700 mb-2">
                <strong>🔄 Auto-Sync Enabled:</strong> Role assignments are automatically managed based on active memberships.
              </p>
              <p className="text-xs text-blue-600">
                When you assign memberships below, the corresponding roles will be automatically activated.
              </p>
            </div>
            
            {/* Legacy Membership Level (for backward compatibility) */}
            <div>
              <Label htmlFor="membership">Legacy Membership Level</Label>
              <Input
                id="membership"
                value={editableUser.membership_level || ''}
                onChange={(e) => handleInputChange('membership_level', e.target.value)}
                placeholder="e.g., free, premium, pro"
              />
              <p className="text-xs text-muted-foreground mt-1">
                This field is for backward compatibility. Use Active Memberships below for better control.
              </p>
            </div>

            {/* Roles Section */}
            <div>
              <Label>Active Roles</Label>
              <p className="text-sm text-muted-foreground mb-3">
                Assign user roles to control dashboard access and permissions. Use the radio button to select which role should be primary.
              </p>
              {rolesLoading ? (
                <div className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span className="text-sm text-muted-foreground">Loading roles...</span>
                </div>
              ) : (
                <div className="space-y-2">
                  <RadioGroup value={primaryRole || ''} onValueChange={setPrimaryRole}>
                    {roles.map(role => (
                      <div key={role.slug} className="flex items-center space-x-3 p-2 rounded-md border">
                        <Checkbox 
                          checked={selectedRoles.includes(role.slug)}
                          onCheckedChange={(checked) => handleRoleToggle(role.slug, checked as boolean)}
                        />
                        <RadioGroupItem 
                          value={role.slug}
                          id={`primary-role-${role.slug}`}
                          disabled={!selectedRoles.includes(role.slug)}
                        />
                        <Label 
                          htmlFor={`primary-role-${role.slug}`}
                          className="flex-1 cursor-pointer"
                        >
                          <div className="flex items-center gap-2">
                            {role.name}
                            {primaryRole === role.slug && (
                              <Badge variant="secondary" className="bg-yellow-100 text-yellow-800">
                                <Crown className="h-3 w-3 mr-1" />
                                Primary
                              </Badge>
                            )}
                          </div>
                        </Label>
                      </div>
                    ))}
                  </RadioGroup>
                </div>
              )}
            </div>

            {/* Memberships Section */}
            <div>
              <Label>Active Memberships</Label>
               <p className="text-sm text-muted-foreground mb-3">
                 Assign membership levels to control access to premium features. Use the radio buttons to select which membership should be primary (determines main role and permissions).
               </p>
              {assignmentsLoading ? (
                <div className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span className="text-sm text-muted-foreground">Loading memberships...</span>
                </div>
              ) : (
                <div className="space-y-4">
                    <MultiMembershipSelector
                      availableMemberships={memberships as SupabaseMembership[]}
                      currentMemberships={profileMemberships}
                      selectedMemberships={selectedMemberships}
                      primaryMembership={primaryMembership}
                      onAssignMembership={handleAssignMembership}
                      onRemoveMembership={handleRemoveMembership}
                      onSetPrimary={handleSetPrimary}
                      isLoading={isUpdating}
                     />
                 </div>
              )}
            </div>
          </div>

          {localError && (
            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700">
              <AlertCircle className="h-4 w-4" />
              <span>{localError}</span>
            </div>
          )}
        </>
      ) : (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin" />
          <span className="ml-2">Loading user data...</span>
        </div>
      )}
    </div>
  );

  const footer = (
    <div className="flex justify-between gap-2 pt-4 border-t">
      <Button 
        variant="destructive" 
        onClick={async () => {
          if (!editableUser?.id) return;
          
          if (!confirm(`Are you sure you want to permanently delete ${editableUser.name}? This will delete their account and all associated data. This action cannot be undone.`)) {
            return;
          }

          try {
            setIsUpdating(true);
            const { data, error } = await invokeEdgeFunction('delete-user', {
              body: { userIds: [editableUser.id] }
            });

            if (error) {
              console.error('Error deleting user:', error);
              toast.error('Failed to delete user');
              return;
            }

            if (data.errors && data.errors.length > 0) {
              toast.error('Failed to delete user. Check console for details.');
              console.error('❌ Deletion error:', data.errors);
              // Log detailed error info
              data.errors.forEach((err: any, index: number) => {
                console.error(`   Error ${index + 1}:`, {
                  profileId: err.profileId,
                  userId: err.userId,
                  error: err.error
                });
              });
            } else {
              toast.success('User deleted successfully');
              onClose();
              if (onDataRefresh) onDataRefresh();
            }
          } catch (err) {
            console.error('Error deleting user:', err);
            toast.error('An unexpected error occurred');
          } finally {
            setIsUpdating(false);
          }
        }}
        disabled={isUpdating}
      >
        <UserX className="mr-2 h-4 w-4" />
        Delete User
      </Button>
      <div className="flex gap-2">
        <Button variant="outline" onClick={onClose} disabled={isUpdating}>
          Cancel
        </Button>
        <Button onClick={handleSave} disabled={isUpdating || !editableUser}>
          {isUpdating ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Updating
            </>
          ) : (
            <>
              <Save className="mr-2 h-4 w-4" />
              Save Changes
            </>
          )}
        </Button>
      </div>
    </div>
  );

  if (inline) {
    return (
      <>
        {content}
        {footer}
        <AdminPasswordReset
          userId={editableUser?.id || ''}
          userEmail={editableUser?.email || ''}
          userName={editableUser?.name || ''}
          open={passwordResetOpen}
          onOpenChange={setPasswordResetOpen}
        />
      </>
    );
  }

  return (
    <Drawer open={isOpen} onOpenChange={onClose}>
      <DrawerContent>
        <DrawerHeader className="flex justify-between items-center">
          <DrawerTitle>Edit User Details</DrawerTitle>
          <DrawerClose asChild>
            <Button variant="ghost" size="sm">
              <X className="h-4 w-4" />
            </Button>
          </DrawerClose>
        </DrawerHeader>
        <div className="p-4 max-h-[70vh] overflow-y-auto">
          {content}
        </div>
        <DrawerFooter>
          <div className="flex justify-between gap-2 w-full">
            <Button 
              variant="destructive" 
              onClick={async () => {
                if (!editableUser?.id) return;
                
                if (!confirm(`Are you sure you want to permanently delete ${editableUser.name}? This will delete their account and all associated data. This action cannot be undone.`)) {
                  return;
                }

                try {
                  setIsUpdating(true);
                  const { data, error } = await invokeEdgeFunction('delete-user', {
                    body: { userIds: [editableUser.id] }
                  });

                  if (error) {
                    console.error('Error deleting user:', error);
                    toast.error('Failed to delete user');
                    return;
                  }

                  if (data.errors && data.errors.length > 0) {
                    toast.error('Failed to delete user. Check console for details.');
                    console.error('❌ Deletion error:', data.errors);
                    // Log detailed error info
                    data.errors.forEach((err: any, index: number) => {
                      console.error(`   Error ${index + 1}:`, {
                        profileId: err.profileId,
                        userId: err.userId,
                        error: err.error
                      });
                    });
                  } else {
                    toast.success('User deleted successfully');
                    onClose();
                    if (onDataRefresh) onDataRefresh();
                  }
                } catch (err) {
                  console.error('Error deleting user:', err);
                  toast.error('An unexpected error occurred');
                } finally {
                  setIsUpdating(false);
                }
              }}
              disabled={isUpdating}
            >
              <UserX className="mr-2 h-4 w-4" />
              Delete User
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={onClose} disabled={isUpdating}>
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={isUpdating || !editableUser}>
                {isUpdating ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Updating
                  </>
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    Save Changes
                  </>
                )}
              </Button>
            </div>
          </div>
        </DrawerFooter>
      </DrawerContent>

      <AdminPasswordReset
        userId={editableUser?.id || ''}
        userEmail={editableUser?.email || ''}
        userName={editableUser?.name || ''}
        open={passwordResetOpen}
        onOpenChange={setPasswordResetOpen}
      />
    </Drawer>
  );
};

export default EditableUserDetailDrawer;
